import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { FileSearch, Plus, Search, Eye, Calendar, Building, DollarSign } from 'lucide-react';

function Tenders() {
  const { API } = useContext(AppContext);
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    fetchTenders();
  }, [activeTab]);

  const fetchTenders = async () => {
    try {
      const response = await axios.get(`${API}/stores/tenders`);
      setTenders(response.data);
    } catch (error) {
      console.error('Error fetching tenders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700 border-green-200';
      case 'Closed': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'Awarded': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Tender': return 'bg-purple-100 text-purple-700';
      case 'Enquiry': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const stats = {
    active: tenders.filter(t => t.status === 'Active').length,
    closed: tenders.filter(t => t.status === 'Closed').length,
    awarded: tenders.filter(t => t.status === 'Awarded').length,
    totalValue: tenders.filter(t => t.status === 'Active').reduce((sum, t) => sum + t.estimatedValue, 0),
  };

  if (loading) return <div className="text-center py-12">Loading tenders...</div>;

  return (
    <div className="space-y-6" data-testid="tenders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenders & Enquiries</h1>
          <p className="text-gray-500 mt-1">Manage procurement tenders and rate enquiries</p>
        </div>
        <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
          <Plus size={18} /><span>Create New</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600 font-medium">Active</p>
          <p className="text-2xl font-bold text-green-700">{stats.active}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200">
          <p className="text-sm text-gray-600 font-medium">Closed</p>
          <p className="text-2xl font-bold text-gray-700">{stats.closed}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Awarded</p>
          <p className="text-2xl font-bold text-blue-700">{stats.awarded}</p>
        </div>
        <div className="bg-gradient-to-br from-[#1a237e]/5 to-[#283593]/10 rounded-xl p-5 border border-[#1a237e]/20">
          <p className="text-sm text-[#1a237e] font-medium">Active Value</p>
          <p className="text-2xl font-bold text-[#1a237e]">₹{(stats.totalValue / 100000).toFixed(1)}L</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-2">
        {['active', 'closed', 'awarded', 'all'].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${activeTab === tab ? 'bg-[#1a237e] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Tenders Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tenders.filter(t => activeTab === 'all' || t.status.toLowerCase() === activeTab).map((tender) => (
          <div key={tender.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getTypeColor(tender.type)}`}>{tender.type}</span>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(tender.status)}`}>{tender.status}</span>
              </div>
              <span className="font-mono text-sm text-[#1a237e]">{tender.id}</span>
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-2">{tender.title}</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2 text-gray-600">
                <Building size={14} />
                <span>Category: {tender.category}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <DollarSign size={14} />
                <span>Est. Value: <span className="font-semibold text-gray-900">₹{(tender.estimatedValue / 100000).toFixed(1)}L</span></span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <Calendar size={14} />
                <span>Deadline: <span className={`font-medium ${new Date(tender.submissionDeadline) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>{tender.submissionDeadline}</span></span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-500">{tender.responses} responses received</span>
              <button className="flex items-center space-x-1 text-[#1a237e] font-medium text-sm hover:text-[#e65100]">
                <Eye size={16} /><span>View Details</span>
              </button>
            </div>

            {tender.awardedTo && (
              <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700"><span className="font-medium">Awarded to:</span> {tender.awardedTo}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Tenders;
